import { GameState }       from '../../core/GameState.js';
import { RESEARCH_TREE, PERMIT_TREE } from '../../config/research.js';
import { formatMoney }     from '../../utils/formatting.js';
import { showAlert, showConfirm } from '../components/dialogs.js';
import { serverIntent } from '../../core/serverIntent.js';

/**
 * Research & Permits tab with sub-tab navigation.
 */
export class ResearchPanel {
  constructor(uiManager) {
    this.ui = uiManager;
    this._activeSubTab = 'research'; // 'research' | 'permits'
  }

  render() {
    const container = document.querySelector('.panel-content');

    // Sub-tab bar
    const rActive = this._activeSubTab === 'research' ? 'active' : '';
    const pActive = this._activeSubTab === 'permits'  ? 'active' : '';

    let html = `<div class="research-subtabs">
      <button class="rtab ${rActive}" data-rtab="research">Research</button>
      <button class="rtab ${pActive}" data-rtab="permits">Permits</button>
    </div>
    <div class="research-subtab-content"></div>`;

    container.innerHTML = html;

    // Sub-tab switching
    container.querySelectorAll('.rtab').forEach(btn => {
      btn.addEventListener('click', () => {
        this._activeSubTab = btn.dataset.rtab;
        this.render();
      });
    });

    const content = container.querySelector('.research-subtab-content');
    if (this._activeSubTab === 'research') {
      this._renderResearch(content);
    } else {
      this._renderPermits(content);
    }
  }

  // ── Research sub-tab ────────────────────────────────────
  _renderResearch(el) {
    let progressHtml = '';
    if (GameState.research.current) {
      const tech = RESEARCH_TREE[GameState.research.current];
      const pct  = tech ? Math.round((GameState.research.progress / tech.researchTime) * 100) : 0;
      progressHtml = `<div class="research-current">
        <strong>Researching: ${tech?.name || '?'}</strong>
        <div class="progress-bar-track"><div class="progress-bar-fill" style="width:${pct}%;background:#e67e22"></div></div>
        <span>${GameState.research.progress}/${tech?.researchTime || '?'} days</span>
        <button class="btn btn-sm btn-danger" id="btn-cancel-research">Cancel</button>
      </div>`;
    }

    const tiers = {};
    for (const [id, tech] of Object.entries(RESEARCH_TREE)) {
      (tiers[tech.tier] = tiers[tech.tier] || []).push({ id, ...tech });
    }

    let treeHtml = '';
    for (const [tier, techs] of Object.entries(tiers).sort((a, b) => a[0] - b[0])) {
      treeHtml += `<div class="research-tier"><h5>Tier ${tier}</h5><div class="research-tier-items">`;
      for (const tech of techs) {
        const done     = GameState.research.completed.has(tech.id);
        const current  = GameState.research.current === tech.id;
        const avail    = this._isResearchAvailable(tech.id);

        // Only one research can run at a time — every other card greys out
        // (not just loses its button) while one is in progress, same as an
        // unmet-prerequisite lock.
        const blockedByOther = !!GameState.research.current && !current;

        let cls = 'research-card';
        if (done)    cls += ' done';
        if (current) cls += ' active';
        if (!done && (!avail || blockedByOther)) cls += ' locked';

        let btn = '';
        if (!done && !current && avail && !blockedByOther) {
          btn = `<button class="btn btn-sm btn-primary" data-research="${tech.id}">Research</button>`;
        }

        const effectDesc = this._formatEffects(tech.effects);

        treeHtml += `<div class="${cls}">
          <strong>${tech.name}</strong>
          <p>${tech.description}</p>
          ${effectDesc ? `<div class="research-effects">${effectDesc}</div>` : ''}
          <small>Cost: ${formatMoney(tech.cost)} &nbsp; Time: ${tech.researchTime}d</small>
          ${btn}
        </div>`;
      }
      treeHtml += '</div></div>';
    }

    el.innerHTML = progressHtml + treeHtml;

    // Cancel button
    const cancelBtn = document.getElementById('btn-cancel-research');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', async () => {
        await serverIntent('research/cancel', {});
        this.render();
      });
    }

    // Research buttons
    el.querySelectorAll('[data-research]').forEach(btn => {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        const result = await serverIntent('research/start', { techId: btn.dataset.research });
        btn.disabled = false;
        if (result.success) {
          this.render();
        }
      });
    });
  }

  // ── Permits sub-tab ─────────────────────────────────────
  _renderPermits(el) {
    let progressHtml = '';
    if (GameState.permits.current) {
      const permit = PERMIT_TREE[GameState.permits.current];
      const pct = permit ? Math.round((GameState.permits.progress / permit.researchTime) * 100) : 0;
      progressHtml = `<div class="research-current permit-progress">
        <strong>Applying: ${permit?.name || '?'}</strong>
        <div class="progress-bar-track"><div class="progress-bar-fill" style="width:${pct}%;background:#9b59b6"></div></div>
        <span>${GameState.permits.progress}/${permit?.researchTime || '?'} days</span>
        <button class="btn btn-sm btn-danger" id="btn-cancel-permit">Cancel</button>
        <button class="btn btn-sm btn-warning" data-bribe-permit="${GameState.permits.current}">
          Expedite (${formatMoney(permit?.bribeCost || 0)}, ${Math.round((permit?.bribeSuccessRate || 0) * 100)}%)
        </button>
      </div>`;
    }

    // Rejection notice
    let rejectionHtml = '';
    if (GameState.permits.rejected && GameState.permits.rejectedUntil) {
      const now = GameState.currentDate.getTime();
      const until = new Date(GameState.permits.rejectedUntil).getTime();
      if (now < until) {
        const daysLeft = Math.ceil((until - now) / (24 * 60 * 60 * 1000));
        const permit = PERMIT_TREE[GameState.permits.rejected];
        rejectionHtml = `<div class="permit-rejected-notice">
          ${permit?.name || GameState.permits.rejected}: Application rejected — ${daysLeft} day cooldown remaining
        </div>`;
      }
    }

    const tiers = {};
    for (const [id, permit] of Object.entries(PERMIT_TREE)) {
      (tiers[permit.tier] = tiers[permit.tier] || []).push({ id, ...permit });
    }

    let treeHtml = '';
    for (const [tier, permits] of Object.entries(tiers).sort((a, b) => a[0] - b[0])) {
      treeHtml += `<div class="research-tier"><h5>Tier ${tier}</h5><div class="research-tier-items">`;
      for (const permit of permits) {
        const done    = GameState.permits.completed.has(permit.id);
        const current = GameState.permits.current === permit.id;
        const avail   = this._isPermitAvailable(permit.id);

        // Check if this specific permit is in cooldown
        const inCooldown = GameState.permits.rejected === permit.id &&
          GameState.permits.rejectedUntil &&
          GameState.currentDate.getTime() < new Date(GameState.permits.rejectedUntil).getTime();

        // Only one permit application can run at a time — same grey-out
        // treatment as the Research tab for every other card while one is
        // in progress.
        const blockedByOther = !!GameState.permits.current && !current;

        let cls = 'research-card permit-card';
        if (done)    cls += ' done';
        if (current) cls += ' active';
        if (!done && (!avail || blockedByOther)) cls += ' locked';
        if (inCooldown) cls += ' rejected';

        let btn = '';
        if (!done && !current && avail && !blockedByOther) {
          btn = `<button class="btn btn-sm btn-primary" data-permit="${permit.id}">Apply</button>`;
          btn += ` <button class="btn btn-sm btn-warning" data-bribe-permit="${permit.id}">
            Expedite (${formatMoney(permit.bribeCost)}, ${Math.round(permit.bribeSuccessRate * 100)}%)
          </button>`;
        }

        treeHtml += `<div class="${cls}">
          <strong>${permit.name}</strong>
          <p>${permit.description}</p>
          <small>Cost: ${formatMoney(permit.cost)} &nbsp; Time: ${permit.researchTime}d</small>
          ${inCooldown ? '<div class="permit-cooldown-label">Cooldown active</div>' : ''}
          ${btn}
        </div>`;
      }
      treeHtml += '</div></div>';
    }

    el.innerHTML = progressHtml + rejectionHtml + treeHtml;

    // Cancel permit
    const cancelBtn = document.getElementById('btn-cancel-permit');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', async () => {
        await serverIntent('research/cancel-permit', {});
        this.render();
      });
    }

    // Apply permit buttons
    el.querySelectorAll('[data-permit]').forEach(btn => {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        const result = await serverIntent('research/permit', { permitId: btn.dataset.permit });
        btn.disabled = false;
        if (result.success) {
          this.render();
        }
      });
    });

    // Bribe/expedite buttons
    el.querySelectorAll('[data-bribe-permit]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const permitId = btn.dataset.bridePermit || btn.dataset.bridePermit;
        // getAttribute to handle the data attribute name
        const id = btn.getAttribute('data-bribe-permit');
        const permit = PERMIT_TREE[id];
        if (!permit) return;

        const confirmMsg = `Expedite "${permit.name}"?<br><br>Cost: ${formatMoney(permit.bribeCost)}<br>Success chance: ${Math.round(permit.bribeSuccessRate * 100)}%<br><br>If failed: Fine of ${formatMoney(permit.bribeCost * permit.bribeFineMultiplier)} + ${permit.bribeCooldown}-day cooldown.`;
        showConfirm(confirmMsg, { title: 'Expedite Permit', confirmLabel: 'Expedite' }).then(async ok => {
          if (!ok) return;
          const result = await serverIntent('research/bribe-permit', { permitId: id });
          if (result.success) {
            showAlert(`Success! ${permit.name} granted immediately.`, 'success');
          }
          this.render();
        });
      });
    });
  }

  // ── Research availability helpers ──────────────────────
  // These replace the server-side ResearchSystem.isAvailable/isPermitAvailable
  // calls that now go through the stub proxy in Phase 6.

  _isResearchAvailable(techId) {
    const tech = RESEARCH_TREE[techId];
    if (!tech) return false;
    if (GameState.research.completed.has(techId)) return false;
    if (!tech.prerequisites) return true;
    return tech.prerequisites.every(p => GameState.research.completed.has(p));
  }

  _isPermitAvailable(permitId) {
    const permit = PERMIT_TREE[permitId];
    if (!permit) return false;
    if (GameState.permits.completed.has(permitId)) return false;
    if (!permit.prerequisites) return true;
    return permit.prerequisites.every(p => GameState.permits.completed.has(p));
  }

  // ── Helpers ─────────────────────────────────────────────
  _formatEffects(effects) {
    if (!effects) return '';
    const parts = [];
    if (effects.speedModifier) parts.push(`Speed ${effects.speedModifier > 1 ? '+' : ''}${Math.round((effects.speedModifier - 1) * 100)}%`);
    if (effects.cargoCapacityModifier) parts.push(`Capacity ${effects.cargoCapacityModifier > 1 ? '+' : ''}${Math.round((effects.cargoCapacityModifier - 1) * 100)}%`);
    if (effects.fuelConsumptionModifier) parts.push(`Fuel ${Math.round((effects.fuelConsumptionModifier - 1) * 100)}%`);
    if (effects.loadingTimeModifier) parts.push(`Loading time ${Math.round((effects.loadingTimeModifier - 1) * 100)}%`);
    if (effects.loadingFeeModifier) parts.push(`Loading fees ${Math.round((effects.loadingFeeModifier - 1) * 100)}%`);
    if (effects.crewCostModifier) parts.push(`Crew cost ${Math.round((effects.crewCostModifier - 1) * 100)}%`);
    return parts.length ? `<small class="research-effect-tags">${parts.join(' · ')}</small>` : '';
  }
}
